import type { ReactNode } from 'react';
import type { I18nConsumeProps } from './const';
import { Fragment, useContext, useEffect, useState } from 'react';
import intlInstance from 'react-intl-universal';
import { I18nContext } from './context';

/**
 * 全局已加载的命名空间缓存
 * 记录已加载的 namespace，格式：`${locale}_${namespace}`
 * 如 'en_login', 'zh-cn_price-ui'
 */
const globalLoadedNamespaces = new Set<string>();

/**
 * 加载指定语言的文案数据
 * 支持三种 loader 类型：异步函数、同步函数、直接传对象
 */
async function loadLocaleData(locale: string, loader: any): Promise<any> {
  let rawData;

  if (typeof loader === 'function') {
    // loader 是函数，调用它（可能返回 Promise 或同步值）
    rawData = await loader();
  } else {
    // loader 是对象，直接使用
    rawData = loader;
  }

  // 处理 ES Module 的 default 导出
  return rawData.default || rawData;
}

/**
 * I18nConsumer - 消费文案的包装组件
 *
 * 功能：
 * 1. 从 I18nContext 获取当前语言
 * 2. 根据语言加载对应文案（支持异步函数、同步函数、直接传对象）
 * 3. 通过 namespace 避免重复加载（同一 namespace 的文案只加载一次）
 * 4. 初始化时显示 fallback，语言切换时继续显示当前内容
 * 5. 语言切换时自动重新加载并触发重渲染
 *
 * @example
 * ```tsx
 * import { I18nConsumer } from '@wegic/i18n-core';
 * import { loginI18nLoaders, LOGIN_I18N_NAMESPACE } from './i18n/loaders';
 *
 * function LoginForm() {
 *   return (
 *     <I18nConsumer loaders={loginI18nLoaders} namespace={LOGIN_I18N_NAMESPACE}>
 *       <LoginFormContent />
 *     </I18nConsumer>
 *   );
 * }
 * ```
 */
export function I18nConsumer({ loaders, children, fallback = null, namespace }: I18nConsumeProps): ReactNode {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('I18nConsume must be used within I18nContext.Provider');
  }

  const { locale, fallbackLocale } = context;
  // 是否已初始化（区分首次加载和切换语言）
  const [initialized, setInitialized] = useState(false);
  // 当前已加载完成的 locale（用于 key 触发重渲染）
  const [loadedLocale, setLoadedLocale] = useState<string | null>(null);

  useEffect(() => {
    // 如果有 namespace，检查缓存
    if (namespace) {
      const cacheKey = `${locale}_${namespace}`;

      // 已加载过，直接标记完成
      if (globalLoadedNamespaces.has(cacheKey)) {
        setLoadedLocale(locale);
        setInitialized(true);
        return;
      }
    } else {
      // 没有 namespace，给出警告（但不阻止加载）
      console.warn('[I18nConsumer] namespace is recommended to avoid duplicate loading');
    }

    // 注意：切换语言时不重置 initialized，保持显示当前内容

    // 封装加载单个语言的函数
    const loadSingleLocale = async (targetLocale: string): Promise<any | null> => {
      const loader = loaders[targetLocale];
      if (!loader) {
        return null;
      }

      try {
        const data = await loadLocaleData(targetLocale, loader);
        return data;
      } catch (error) {
        console.warn(
          `[I18nConsumer] Failed to load locale "${targetLocale}"${namespace ? ` (namespace: ${namespace})` : ''}`,
          error,
        );
        return null;
      }
    };

    // 主加载逻辑
    const loadData = async () => {
      // 第一次尝试：加载主语言
      let data = await loadSingleLocale(locale);
      let loadedLang = locale;

      // 第二次尝试：如果主语言失败且有 fallbackLocale，加载 fallback
      if (!data && fallbackLocale && fallbackLocale !== locale) {
        data = await loadSingleLocale(fallbackLocale);
        if (data) {
          loadedLang = fallbackLocale;
          console.warn(`[I18nConsumer] Using fallback locale "${fallbackLocale}" for namespace "${namespace}"`);
        }
      }

      // 如果都失败了
      if (!data) {
        console.error(
          `[I18nConsumer] No locale data available for "${locale}"${namespace ? ` (namespace: ${namespace})` : ''}`,
        );
        setLoadedLocale(locale);
        setInitialized(true);
        return;
      }

      // 加载文案到 intl
      intlInstance.load({ [loadedLang]: data });

      // 如果有 namespace，记录到全局缓存
      if (namespace) {
        const cacheKey = `${locale}_${namespace}`;
        globalLoadedNamespaces.add(cacheKey);
      }

      setLoadedLocale(locale);
      setInitialized(true);
    };

    loadData().catch(error => {
      console.error(`[I18nConsumer] Unexpected error${namespace ? ` (namespace: ${namespace})` : ''}:`, error);
      setLoadedLocale(locale);
      setInitialized(true);
    });
  }, [locale, loaders, namespace, fallbackLocale]);

  // 首次加载时显示 fallback
  if (!initialized) {
    return fallback;
  }

  // 已初始化后，使用 loadedLocale 作为 key 触发重渲染
  // 切换语言时继续显示当前内容，加载完成后更新 key 触发重渲染
  return <Fragment key={loadedLocale}>{children}</Fragment>;
}
